/**
 * RegistrationWizard (v2) - Multi-step registration component
 *
 * Migrated to reusable wizard architecture following DocumentTemplateCreateWizard pattern.
 * Uses useWizardNavigation for navigation state and separate domain-specific state.
 *
 * Architecture:
 * - useWizardNavigation: Abstract navigation state (step, buttons, validation triggers)
 * - useRegistrationData: Domain-specific state (parent1, player, emergency contacts)
 * - WizardShell: Pure UI component for wizard chrome
 * - Step components: Validate and communicate via onValidationChange callback
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Trophy } from 'lucide-react';
import PageLayout from '../../PageLayout';
import PageHeader from '../../PageHeader';
import { WizardShell } from '../../club-documents/v2/WizardShell';
import { useWizardNavigation } from '../../wizard-framework';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenant } from '../../../contexts/TenantContext';
import { useToast } from '../../../contexts/ToastContext';
import apiService from '../../../services/api';
import LoadingState from '../../LoadingState';
import styles from './RegistrationWizard.module.css';

// Import step components
import Step2ParentForm from './Step2ParentForm';
import Step4PlayerForm from './Step4PlayerForm';
import Step5EmergencyContactForm from './Step5EmergencyContactForm';

const TOTAL_STEPS = 3; // Steps 2, 4, 5 (step 1 is login, step 3 removed)

const RegistrationWizard = () => {
  const { clubSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { tenant, clubDisplayName, clubColors, clubDescription } = useTenant();
  const { showInfo } = useToast();
  const {
    registerStep2,
    registerStep4,
    registerStep5,
    saveAndExit,
    getRegistrationProgress,
    isRegistrationComplete,
    user,
    loading: authLoading
  } = useAuth();

  const hasLoadedProgressRef = useRef(false);
  const [errors, setErrors] = useState({});
  const [offerToken, setOfferToken] = useState(null);
  const [offerInfo, setOfferInfo] = useState(null);

  // Check for offer registration token in URL
  useEffect(() => {
    const offerParam = searchParams.get('offer');
    if (offerParam) {
      console.log('RegistrationWizard: Detected offer token:', offerParam);
      setOfferToken(offerParam);
      // Optionally fetch offer details to show context
      // This is for display purposes only - user still needs to complete registration
    }
  }, [searchParams]);

  // Check if this is a tryout registration URL and redirect to dedicated tryout route
  useEffect(() => {
    const registration_type = searchParams.get('type');
    if (registration_type === 'tryout') {
      console.log('RegistrationWizard: Detected tryout type, redirecting to dedicated tryout route');
      const newPath = clubSlug ? `/tryouts/register/${clubSlug}` : '/tryouts/register';
      navigate(newPath, { replace: true });
      return;
    }
  }, [searchParams, clubSlug, navigate]);

  // Domain-specific registration data state
  const [registrationData, setRegistrationData] = useState({
    parent1: {
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      address: {
        address_line1: '',
        address_line2: '',
        city: '',
        state: '',
        zip_code: ''
      },
      relationship_to_player: ''
    },
    player: {
      first_name: '',
      last_name: '',
      date_of_birth: '',
      gender: '',
      school: '',
      grade: '',
      previous_experience: '',
      medical_conditions: ''
    },
    emergency_contact: {
      parent_emergency_contacts: {},
      use_additional_contact: false,
      additional_emergency_contact: {
        name: '',
        phone: '',
        relationship: ''
      }
    }
  });

  // Abstract wizard navigation state
  const {
    currentStep,
    goToNext,
    goToPrevious,
    setCanAdvance,
    setIsLoading,
    buttonStates
  } = useWizardNavigation(TOTAL_STEPS, {
    initialStep: 1, // Maps to registration step 2
    onStepChange: (step) => {
      console.log('[Registration Wizard] Navigated to step:', step);
      setErrors({});
    }
  });

  // Map wizard step (1-3) to registration step (2, 4, 5)
  // Step 3 (parent2) has been removed
  const getRegistrationStep = useCallback(() => {
    if (currentStep === 1) return 2; // Parent 1
    if (currentStep === 2) return 4; // Player
    if (currentStep === 3) return 5; // Emergency Contact
    return 2;
  }, [currentStep]);

  // Handle data changes from step components
  const handleDataChange = useCallback((newData) => {
    setRegistrationData(newData);
  }, []);

  // Handle validation changes from step components
  const handleValidationChange = useCallback((isValid) => {
    setCanAdvance(isValid);
  }, [setCanAdvance]);

  // Load registration progress on component mount
  const loadRegistrationProgress = useCallback(async () => {
    try {
      const progress = await getRegistrationProgress();
      if (progress) {
        // Backend returns the last completed step
        const currentBackendStep = progress.step || 1;
        const nextStepToShow = currentBackendStep + 1;

        // Map backend step (2-5) to wizard step (1-4)
        const wizardStep = Math.max(1, Math.min(4, nextStepToShow - 1));

        // Initialize with saved data if available
        if (progress.data) {
          console.log('📥 Initializing wizard with saved data:', progress.data);
          setRegistrationData(prev => ({
            ...prev,
            ...progress.data
          }));
        } else if (user?.email) {
          // Prepopulate parent1 email with user's email if no saved data
          console.log('📧 Prepopulating parent email with user email:', user.email);
          setRegistrationData(prev => ({
            ...prev,
            parent1: {
              ...prev.parent1,
              email: user.email
            }
          }));
        }

        console.log('Registration progress loaded:', {
          backendStep: currentBackendStep,
          wizardStep,
          completedSteps: progress.completed_steps,
          hasData: !!progress.data
        });
      } else if (user?.email) {
        // No progress data at all - prepopulate with user email
        console.log('📧 First time registration - prepopulating with user email:', user.email);
        setRegistrationData(prev => ({
          ...prev,
          parent1: {
            ...prev.parent1,
            email: user.email
          }
        }));
      }
    } catch (error) {
      console.error('Failed to load registration progress:', error);
    }
  }, [getRegistrationProgress, user]);

  // Load registration progress and check authentication
  useEffect(() => {
    if (authLoading) return;

    // If user is not authenticated, redirect to auth page
    if (!user) {
      const currentUrl = window.location.pathname + window.location.search;
      const redirectUrl = encodeURIComponent(currentUrl);
      navigate(`/auth?redirect=${redirectUrl}`, {
        state: { from: currentUrl }
      });
      return;
    }

    // If user has completed registration, show toast and redirect to dashboard
    if (isRegistrationComplete()) {
      showInfo('Your registration is already complete. Redirecting to dashboard...');
      setTimeout(() => navigate('/dashboard'), 1500);
      return;
    }

    // Load registration progress only once
    if (!hasLoadedProgressRef.current) {
      hasLoadedProgressRef.current = true;
      loadRegistrationProgress();
    }
  }, [authLoading, user, navigate, loadRegistrationProgress, isRegistrationComplete, showInfo]);

  // Handle next button - save current step and advance
  const handleNext = async () => {
    const regStep = getRegistrationStep();
    setIsLoading(true);
    setErrors({});

    try {
      let result;

      switch (regStep) {
        case 2:
          // Step 2: Save Parent 1 information
          console.log('💾 Saving Step 2 data...');
          result = await registerStep2({ parent1: registrationData.parent1 });
          break;

        case 4:
          // Step 4: Save Player information
          console.log('💾 Saving Step 4 data...');
          result = await registerStep4({ player: registrationData.player });
          break;

        default:
          result = { success: false, error: 'Invalid step' };
      }

      if (result.success) {
        console.log(`✅ Step ${regStep} saved successfully`);
        goToNext();
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle finish - complete emergency contact setup and registration
  const handleFinish = async () => {
    setIsLoading(true);
    setErrors({});

    try {
      // Build emergency_contacts array in the format expected by backend
      const emergency_contacts = [];

      // Add parent1 as emergency contact if selected
      if (registrationData.emergency_contact.parent_emergency_contacts?.parent1 && user?.id) {
        emergency_contacts.push({
          type: 'user',
          user_id: user.id,
          relationship: registrationData.parent1.relationship_to_player || 'Parent/Guardian'
        });
      }

      // Add additional emergency contact if provided
      if (registrationData.emergency_contact.use_additional_contact) {
        const additionalContact = registrationData.emergency_contact.additional_emergency_contact;
        if (additionalContact?.name && additionalContact?.phone && additionalContact?.relationship) {
          emergency_contacts.push({
            type: 'external',
            name: additionalContact.name,
            phone: additionalContact.phone,
            relationship: additionalContact.relationship
          });
        }
      }

      const emergencyData = {
        emergency_contacts: emergency_contacts
      };

      console.log('📤 Sending emergency contacts to backend:', emergencyData);

      const result = await registerStep5(emergencyData);

      if (result.success) {
        // If this registration was initiated from an offer, complete the offer registration
        if (offerToken && user?.id) {
          try {
            console.log('Completing offer registration with token:', offerToken);
            await apiService.completeOfferRegistration(offerToken, user.id);
            console.log('✅ Offer registration completed successfully');
            // Navigate with offer flag so RegistrationComplete can show appropriate message
            navigate('/registration-complete?offer=true');
          } catch (offerError) {
            console.error('Failed to complete offer registration:', offerError);
            // Still proceed to registration complete, but without offer flag
            navigate('/registration-complete');
          }
        } else {
          navigate('/registration-complete');
        }
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      console.error('Registration error:', error);
      setErrors({ general: 'An unexpected error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle save and exit
  const handleSaveAndExit = async () => {
    const regStep = getRegistrationStep();
    setIsLoading(true);
    setErrors({});

    try {
      const currentData = regStep === 2 ? registrationData.parent1 : registrationData.player;
      const result = await saveAndExit(regStep, currentData);

      if (result.success) {
        navigate('/dashboard');
      } else {
        setErrors({ general: result.error });
      }
    } catch (error) {
      console.error('Error saving progress:', error);
      setErrors({ general: 'Failed to save progress. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Render current step component
  const renderCurrentStep = () => {
    const regStep = getRegistrationStep();

    switch (regStep) {
      case 2:
        return (
          <Step2ParentForm
            registrationData={registrationData}
            onDataChange={handleDataChange}
            onValidationChange={handleValidationChange}
          />
        );

      case 4:
        return (
          <Step4PlayerForm
            registrationData={registrationData}
            onDataChange={handleDataChange}
            onValidationChange={handleValidationChange}
          />
        );

      case 5:
        return (
          <Step5EmergencyContactForm
            registrationData={registrationData}
            onDataChange={handleDataChange}
            onValidationChange={handleValidationChange}
          />
        );

      default:
        return null;
    }
  };

  // Show loading while authentication is being checked
  if (authLoading) {
    return (
      <PageLayout>
        <LoadingState />
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <PageHeader
        title={tenant && clubDisplayName ? `${clubDisplayName.toUpperCase()} REGISTRATION` : "PARENT REGISTRATION"}
        subtitle={tenant && clubDisplayName ? `Join ${clubDisplayName} - ${clubDescription}` : "Complete your registration to access training programs"}
      />

      {/* Club Banner */}
      {tenant && clubDisplayName && (
        <div
          className={styles.clubBanner}
          style={{
            background: `linear-gradient(135deg, ${clubColors?.primary || 'var(--color-primary)'}20 0%, ${clubColors?.primary || 'var(--color-primary)'}10 100%)`,
            borderColor: `${clubColors?.primary || 'var(--color-primary)'}30`
          }}
        >
          <h2
            className={styles.clubBannerTitle}
            style={{ color: clubColors?.primary || 'var(--color-primary)' }}
          >
            <Trophy size={24} className="inline mr-2" /> Welcome to {clubDisplayName}
          </h2>
          <p className={styles.clubBannerDescription}>
            {clubDescription}
          </p>
          <p className={styles.clubBannerNote}>
            Your registration will be automatically assigned to this club
          </p>
        </div>
      )}

      {/* Error Message */}
      {errors.general && (
        <div className={styles.errorMessage}>
          {errors.general}
        </div>
      )}

      {/* Wizard Shell with Step Content */}
      <WizardShell
        title="Parent Registration"
        totalSteps={TOTAL_STEPS}
        currentStep={currentStep}
        onNext={handleNext}
        onPrevious={goToPrevious}
        onFinish={handleFinish}
        onClose={handleSaveAndExit}
        buttonStates={buttonStates}
        nextButtonLabel="Continue →"
        previousButtonLabel="← Previous"
        finishButtonLabel="Complete Registration"
        isLoading={buttonStates.isLoading}
      >
        {renderCurrentStep()}
      </WizardShell>
    </PageLayout>
  );
};

export default RegistrationWizard;
